// Training school screen - improve gladiator stats

import { Canvas } from '../Canvas';
import { CONSTANTS, ScreenState } from '../types';
import { Screen, ScreenContext } from './Screen';
import { GameState, getCurrentTeam } from '../systems/GameState';
import { GAME_CONSTANTS } from '../data/constants';
import { GladiatorData } from '../entities/Gladiator';

interface TrainingOption {
  id: string;
  name: string;
  statKey: 'str' | 'speed' | 'maxHp' | 'maxMana';
  displayKey: string;
}

const TRAINING_OPTIONS: TrainingOption[] = [
  { id: 'str', name: 'Voima', statKey: 'str', displayKey: 'Voima' },
  { id: 'speed', name: 'Nopeus', statKey: 'speed', displayKey: 'Nopeus' },
  { id: 'hp', name: 'Kestävyys', statKey: 'maxHp', displayKey: 'Kesto' },
  { id: 'mana', name: 'Mana', statKey: 'maxMana', displayKey: 'Mana' },
];

const BASE_TRAINING_COST = 100;
const TRAINING_COST_INCREMENT = 50;

// Calculate training cost based on how many times the stat has been trained
function getTrainingCost(gladiator: GladiatorData | null, statKey: 'str' | 'speed' | 'maxHp' | 'maxMana'): number {
  if (!gladiator) return BASE_TRAINING_COST;
  const timesTrained = gladiator.trainingHistory?.[statKey] ?? 0;
  return BASE_TRAINING_COST + (timesTrained * TRAINING_COST_INCREMENT);
}

export class TrainingSchool implements Screen {
  readonly state = ScreenState.SCHOOL;
  private context: ScreenContext;
  private gameState: GameState | null = null;
  private selectedTrainingIndex: number = 0;
  private selectedGladiatorIndex: number = 0;

  constructor(context: ScreenContext) {
    this.context = context;
  }

  setGameState(gameState: GameState): void {
    this.gameState = gameState;
    this.selectedGladiatorIndex = 0;
  }

  enter(): void {
    console.log('Entering TrainingSchool');
    this.selectedTrainingIndex = 0;
    this.selectedGladiatorIndex = 0;
  }

  exit(): void {
    console.log('Exiting TrainingSchool');
  }

  update(_deltaTime: number): void {}

  render(canvas: Canvas): void {
    if (!this.gameState) {
      canvas.clear(CONSTANTS.SCHOOL_BACKGROUND);
      return;
    }

    const team = getCurrentTeam(this.gameState);
    const gladiator = this.getSelectedGladiator();

    canvas.clear(CONSTANTS.SCHOOL_BACKGROUND);

    // Header
    this.renderHeader(canvas, team);

    // Gladiator info (with integrated picker arrows)
    this.renderGladiatorInfo(canvas, gladiator);

    // Training options
    this.renderTrainingOptions(canvas, gladiator, team);

    // Back button
    this.renderBackButton(canvas);
  }

  private getSelectedGladiator(): GladiatorData | null {
    if (!this.gameState) return null;
    const team = getCurrentTeam(this.gameState);
    if (!team || team.gladiatorIds.length === 0) return null;

    if (this.selectedGladiatorIndex >= team.gladiatorIds.length) {
      this.selectedGladiatorIndex = 0;
    }

    const gladiatorId = team.gladiatorIds[this.selectedGladiatorIndex];
    return this.gameState.gladiators.get(gladiatorId) ?? null;
  }

  private renderHeader(canvas: Canvas, team: any): void {
    canvas.drawRect(0, 0, CONSTANTS.CANVAS_WIDTH, 50, '#006060');

    canvas.drawText('Harjoittelukoulu', 20, 12, CONSTANTS.BIG_FONT, '#ffffff');

    if (team) {
      canvas.drawText(
        `${GAME_CONSTANTS.MONEY_PREFIX}: ${team.money} ${GAME_CONSTANTS.MONEY_SUFFIX}`,
        CONSTANTS.CANVAS_WIDTH - 20,
        12,
        CONSTANTS.BIG_FONT,
        '#ffffff',
        'right'
      );
    }

    // Training info
    canvas.drawText(
      `Hinta nousee harjoittelun myötä`,
      400,
      12,
      CONSTANTS.MAIN_FONT,
      '#ffff00',
      'center'
    );
  }

  private renderGladiatorInfo(canvas: Canvas, gladiator: any): void {
    const infoX = 20;
    const infoY = 60;
    const infoWidth = 350;
    const infoHeight = 480;

    canvas.drawRect(infoX, infoY, infoWidth, infoHeight, '#004040');
    canvas.drawRectOutline(infoX, infoY, infoWidth, infoHeight, '#00aaaa');

    const team = getCurrentTeam(this.gameState!);
    if (!team || team.gladiatorIds.length === 0) {
      canvas.drawText('Ei gladiaattoreita harjoiteltavaksi', infoX + infoWidth / 2, infoY + infoHeight / 2,
        CONSTANTS.MAIN_FONT, '#88cccc', 'center');
      return;
    }

    // Navigation arrows at the top
    const arrowWidth = 40;
    const arrowHeight = 35;
    const arrowY = infoY + 15;

    // Previous arrow
    const canGoPrev = this.selectedGladiatorIndex > 0;
    canvas.drawButton(
      'prev-gladiator',
      '◀',
      infoX + 10,
      arrowY,
      arrowWidth,
      arrowHeight,
      CONSTANTS.BIG_FONT,
      '#ffffff',
      canGoPrev ? '#006666' : '#333333',
      () => {
        if (canGoPrev) {
          this.selectedGladiatorIndex--;
        }
      }
    );

    // Next arrow
    const canGoNext = this.selectedGladiatorIndex < team.gladiatorIds.length - 1;
    canvas.drawButton(
      'next-gladiator',
      '▶',
      infoX + infoWidth - arrowWidth - 10,
      arrowY,
      arrowWidth,
      arrowHeight,
      CONSTANTS.BIG_FONT,
      '#ffffff',
      canGoNext ? '#006666' : '#333333',
      () => {
        if (canGoNext) {
          this.selectedGladiatorIndex++;
        }
      }
    );

    // Counter in the center
    canvas.drawText(
      `${this.selectedGladiatorIndex + 1} / ${team.gladiatorIds.length}`,
      infoX + infoWidth / 2,
      arrowY + 10,
      CONSTANTS.BIG_FONT,
      '#88cccc',
      'center'
    );

    if (!gladiator) return;

    // Portrait (shifted down to make room for arrows)
    const contentY = infoY + 60;
    const portrait = this.context.assets.gladiators[gladiator.race.id];
    if (portrait) {
      canvas.drawImage(portrait, infoX + 15, contentY, 80, 80);
    }

    // Name and race
    canvas.drawText(gladiator.name, infoX + 110, contentY + 10, CONSTANTS.GLADIATOR_NAME_FONT, '#ffffff');
    canvas.drawText(gladiator.race.name, infoX + 110, contentY + 35, CONSTANTS.BIG_FONT, '#88cccc');
    canvas.drawText(`Ikä: ${gladiator.age} vuotta`, infoX + 110, contentY + 60, CONSTANTS.MAIN_FONT, '#88cccc');

    // Current stats
    const statsY = contentY + 90;
    const lineHeight = 28;

    canvas.drawText('Nykyiset tilastot:', infoX + 20, statsY, CONSTANTS.MAIN_FONT, '#ffffff');

    const statDisplays = [
      { name: 'Voima', value: gladiator.str, color: '#ff8888' },
      { name: 'Nopeus', value: gladiator.speed, color: '#88ff88' },
      { name: 'Kesto', value: `${gladiator.hp}/${gladiator.maxHp}`, color: '#8888ff' },
      { name: 'Mana', value: `${gladiator.mana}/${gladiator.maxMana}`, color: '#ff88ff' },
    ];

    for (let i = 0; i < statDisplays.length; i++) {
      const stat = statDisplays[i];
      const y = statsY + 30 + i * lineHeight;

      canvas.drawText(`${stat.name}:`, infoX + 30, y, CONSTANTS.BIG_FONT, '#cccccc');
      canvas.drawText(String(stat.value), infoX + 150, y, CONSTANTS.BIG_FONT, stat.color);

      // Visual bar
      const barX = infoX + 220;
      const barWidth = 100;
      const barHeight = 18;
      const maxStat = 50;

      // Extract numeric value for bar
      const numericValue = typeof stat.value === 'string'
        ? parseInt(stat.value.split('/')[1] || stat.value)
        : stat.value;
      const fillWidth = Math.min(barWidth, (numericValue / maxStat) * barWidth);

      canvas.drawRect(barX, y + 2, barWidth, barHeight, '#002020');
      canvas.drawRect(barX, y + 2, fillWidth, barHeight, stat.color);
      canvas.drawRectOutline(barX, y + 2, barWidth, barHeight, '#00aaaa');
    }

    // Skills preview
    const skillsY = statsY + 30 + statDisplays.length * lineHeight + 20;
    canvas.drawText('Taistelutaidot:', infoX + 20, skillsY, CONSTANTS.MAIN_FONT, '#ffffff');

    const skillColumns = [
      { title: 'Hyökkäys', skills: ['Nyrkki', 'Keihäs', 'Miekka', 'Kirves', 'Nuija'], values: gladiator.skills.meleeAtk },
      { title: 'Puolustus', skills: ['Nyrkki', 'Keihäs', 'Miekka', 'Kirves', 'Nuija'], values: gladiator.skills.meleeDef },
    ];

    const colWidth = 150;
    for (let c = 0; c < skillColumns.length; c++) {
      const col = skillColumns[c];
      const colX = infoX + 30 + c * colWidth;

      canvas.drawText(col.title, colX, skillsY + 22, CONSTANTS.MAIN_FONT, '#ffff00');

      for (let i = 0; i < col.skills.length && i < col.values.length; i++) {
        canvas.drawText(
          `${col.skills[i]}: ${col.values[i]}`,
          colX,
          skillsY + 40 + i * 16,
          CONSTANTS.MSG_FONT,
          '#88cccc'
        );
      }
    }
  }

  private renderTrainingOptions(canvas: Canvas, gladiator: any, team: any): void {
    const optX = 390;
    const optY = 60;
    const optWidth = 385;
    const optHeight = 480;

    canvas.drawRect(optX, optY, optWidth, optHeight, '#004040');
    canvas.drawRectOutline(optX, optY, optWidth, optHeight, '#00aaaa');

    canvas.drawText('Harjoittele ominaisuuksia', optX + optWidth / 2, optY + 15, CONSTANTS.BIG_FONT, '#ffffff', 'center');

    if (!gladiator) {
      canvas.drawText('Valitse ensin gladiaattori', optX + optWidth / 2, optY + optHeight / 2,
        CONSTANTS.MAIN_FONT, '#666666', 'center');
      return;
    }

    const buttonWidth = 340;
    const buttonHeight = 80;
    const startY = optY + 50;
    const spacing = 95;

    for (let i = 0; i < TRAINING_OPTIONS.length; i++) {
      const option = TRAINING_OPTIONS[i];
      const btnY = startY + i * spacing;
      const btnX = optX + (optWidth - buttonWidth) / 2;

      // Calculate cost for this specific stat
      const cost = getTrainingCost(gladiator, option.statKey);
      const canAfford = team && team.money >= cost;

      // Get current stat value
      let currentValue: number;
      if (option.statKey === 'maxHp') {
        currentValue = gladiator.maxHp;
      } else if (option.statKey === 'maxMana') {
        currentValue = gladiator.maxMana;
      } else {
        currentValue = gladiator[option.statKey];
      }

      // Get times trained
      const timesTrained = gladiator.trainingHistory?.[option.statKey] ?? 0;

      // Selection highlight
      if (i === this.selectedTrainingIndex) {
        canvas.drawRect(btnX - 5, btnY - 5, buttonWidth + 10, buttonHeight + 10, '#006666');
      }

      // Button background
      const bgColor = canAfford ? '#008080' : '#444444';
      canvas.drawRect(btnX, btnY, buttonWidth, buttonHeight, bgColor);
      canvas.drawRectOutline(btnX, btnY, buttonWidth, buttonHeight, '#00cccc');

      // Option text
      canvas.drawText(option.name, btnX + 15, btnY + 10, CONSTANTS.BIG_FONT, '#ffffff');

      // Current → New value
      canvas.drawText(
        `${currentValue}  →  ${currentValue + 1}`,
        btnX + 15,
        btnY + 32,
        CONSTANTS.MAIN_FONT,
        '#88ff88'
      );

      // Cost display
      canvas.drawText(
        `Hinta: ${cost} kr`,
        btnX + 15,
        btnY + 52,
        CONSTANTS.MAIN_FONT,
        canAfford ? '#ffff00' : '#ff6666'
      );

      // Times trained
      if (timesTrained > 0) {
        canvas.drawText(
          `(Harjoiteltu ${timesTrained}x)`,
          btnX + 130,
          btnY + 52,
          CONSTANTS.MSG_FONT,
          '#88cccc'
        );
      }

      // Train button
      if (canAfford) {
        canvas.drawButton(
          `train-${option.id}`,
          'Harjoittele',
          btnX + buttonWidth - 110,
          btnY + 25,
          95,
          30,
          CONSTANTS.MAIN_FONT,
          '#ffffff',
          '#006600',
          () => this.trainStat(option)
        );
      }

      // Click region for selection
      canvas.addClickableRegion({
        id: `select-${option.id}`,
        x: btnX,
        y: btnY,
        width: buttonWidth - 110,
        height: buttonHeight,
        onClick: () => {
          this.selectedTrainingIndex = i;
        },
      });
    }
  }

  private renderBackButton(canvas: Canvas): void {
    canvas.drawButton(
      'back',
      'Takaisin',
      20,
      CONSTANTS.CANVAS_HEIGHT - 50,
      100,
      35,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      '#006060',
      () => this.context.onScreenChange(ScreenState.MAIN_MENU)
    );
  }

  private trainStat(option: TrainingOption): void {
    const team = getCurrentTeam(this.gameState!);
    const gladiator = this.getSelectedGladiator();

    if (!team || !gladiator) return;

    // Calculate cost for this specific stat
    const cost = getTrainingCost(gladiator, option.statKey);
    if (team.money < cost) return;

    // Deduct cost
    team.money -= cost;

    // Update training history
    if (!gladiator.trainingHistory) {
      gladiator.trainingHistory = { str: 0, speed: 0, maxHp: 0, maxMana: 0 };
    }
    gladiator.trainingHistory[option.statKey] += 1;

    // Increase stat
    if (option.statKey === 'maxHp') {
      gladiator.maxHp += 1;
      gladiator.hp += 1;
    } else if (option.statKey === 'maxMana') {
      gladiator.maxMana += 1;
      gladiator.mana += 1;
    } else {
      (gladiator as any)[option.statKey] += 1;
    }

    console.log(`Trained ${option.name} for ${cost} kr: now ${gladiator[option.statKey]}`);
  }

  handleClick(x: number, y: number): void {
    console.log(`TrainingSchool click at (${x}, ${y})`);
  }

  handleKeyDown(key: string): void {
    if (key === 'Escape') {
      this.context.onScreenChange(ScreenState.MAIN_MENU);
    } else if (key === 'ArrowUp') {
      this.selectedTrainingIndex = Math.max(0, this.selectedTrainingIndex - 1);
    } else if (key === 'ArrowDown') {
      this.selectedTrainingIndex = Math.min(TRAINING_OPTIONS.length - 1, this.selectedTrainingIndex + 1);
    }
  }
}
